import { BusinessUnit } from "./BusinessUnit";
import { Language } from "./Language";

export class AuthUser {
    id: number;
    language_id: number;
    language_code?: string;
    employee_function_id: number;
    business_unit_id: number;
    name: string;
    phonenumber: string;
    street: string;
    street_number: string;
    city: string;
    country: string;
    image_path: string;
    is_active: boolean;
    has_valid_deactivation: boolean;
    email: string;
    state_updated_at: Date;
    employment_start_date: string;
    employment_end_date: string;
    end_date_is_retire_date: boolean;
    business_unit_permissions: [any];
    has_accepted_terms: boolean;
    has_manuals_to_accept: boolean;
    can_change_division: boolean;
    can_change_app_channel: boolean;
    can_view_poll_results: boolean;
    can_access_datev: boolean;
    can_access_my_service_schedule: boolean;
    business_unit: BusinessUnit;
    /** Languages of the own business unit plus those of the business units with content access. */
    available_languages: Language[];

    constructor(user) {
        if (!user) {
            return;
        }

        this.id = user.id;
        this.language_id = user.language_id;
        this.language_code = user.language_code;
        this.employee_function_id = user.employee_function_id;
        this.business_unit_id = user.business_unit_id;
        this.name = user.name;
        this.phonenumber = user.phonenumber;
        this.street = user.street;
        this.street_number = user.street_number;
        this.city = user.city;
        this.country = user.country;
        this.image_path = user.image_path;
        this.is_active = user.is_active;
        this.has_valid_deactivation = user.has_valid_deactivation;
        this.email = user.email;
        this.state_updated_at = new Date();
        this.employment_start_date = user.employment_start_date;
        this.employment_end_date = user.employment_end_date;
        this.end_date_is_retire_date = user.end_date_is_retire_date;
        this.business_unit_permissions = user.business_unit_permissions;
        this.has_accepted_terms = user.has_accepted_terms;
        this.has_manuals_to_accept = user.has_manuals_to_accept;
        this.can_change_division = user.can_change_division || false;
        this.can_change_app_channel = user.can_change_app_channel || false;
        this.can_view_poll_results = user.can_view_poll_results || false;
        this.can_access_datev = user.can_access_datev ?? false;
        this.can_access_my_service_schedule =
            user.can_access_my_service_schedule ?? true;
        this.business_unit = user.business_unit;
        // A profile from before the API sent available_languages only knows the business unit's languages.
        this.available_languages =
            user.available_languages ?? user.business_unit?.languages ?? [];
    }

    canChangeDivision() {
        return this.can_change_division;
    }

    canChangeAppChannel() {
        return this.can_change_app_channel;
    }

    canViewPollResults() {
        return this.can_view_poll_results;
    }

    updatedAtTimestamp() {
        return this.state_updated_at.valueOf();
    }

    /**
     * Helper method to check if user's business unit has permissions.
     */
    hasPermission(permission: string) {
        return (
            (this.business_unit_permissions ?? []).findIndex(
                (p) => p.name === permission
            ) > -1
        );
    }

    /**
     * Check if the user's current language is one they can choose from.
     */
    hasValidLanguage(): boolean {
        if (!this.language_id || !this.available_languages?.length) {
            return false;
        }
        return this.available_languages.some(
            (lang) => lang.id === this.language_id
        );
    }

    /**
     * Check if the user needs to select a language.
     * Show popup if:
     * 1. No language is set (first login)
     * 2. Current language is not in the user's available languages
     */
    needsLanguageSelection(): boolean {
        return !this.language_id || !this.hasValidLanguage();
    }

    /**
     * Get the default language for the user's division.
     */
    getDivisionDefaultLanguage(): Language | null {
        return (
            this.business_unit?.languages?.find(
                (lang) => lang.id === this.business_unit?.default_language_id
            ) || null
        );
    }

    /**
     * Get an array of language codes in priority order for fallback logic:
     * 1. User's profile language
     * 2. Business unit default language
     * 3. Other business unit languages
     * 4. Languages of the business units with content access
     *
     * @returns Array of language codes (strings)
     */
    getLanguagePriorityArray(): string[] {
        return [
            ...new Set(
                [
                    this.language_code,
                    this.business_unit?.default_language?.code,
                    ...(this.business_unit?.languages?.map(
                        (lang) => lang.code
                    ) || []),
                    ...(this.available_languages?.map((lang) => lang.code) ||
                        [])
                ].filter(Boolean)
            )
        ];
    }
}
